import { useState, useEffect, useCallback } from 'react';
import {
  AUTO_PATH_LAYER,
  CUSTOM_PATH_LAYER,
  UNFINISHED_PATH_LAYER,
  POINTS_CIRCLE_LAYER,
  POINTS_SYMBOL_LAYER,
  DISTANCE_LABELS_LAYER
} from '../constants';
import { useAutoHideDistanceLabels } from './useAutoHideDistanceLabels';

/**
 * VRP Layer Control Hook
 * Enhanced version based on TSP implementation with VRP-specific features
 * @param {Object} map - Mapbox map instance
 * @param {boolean} mapReady - Whether map is ready
 * @param {string} currentMode - Current VRP mode
 * @param {boolean} pathComplete - Whether vehicle paths are complete
 * @param {number} selectedVehicleIndex - Currently selected vehicle index
 * @returns {Object} Layer control state and functions
 */
export const useLayerControl = (map, mapReady, currentMode, pathComplete, selectedVehicleIndex) => {
  // Layer visibility state - VRP specific
  const [layerVisibility, setLayerVisibility] = useState({
    autoPath: true,
    vehiclePaths: true,     // Unified vehicle paths (completed + unfinished)
    locationPoints: true,    // Location markers (circle + symbol layers)
    distanceLabels: true
  });

  // Auto-hide distance labels state
  const [autoHideInfo, setAutoHideInfo] = useState({
    isAutoHidden: false,
    shouldAutoHide: false,
    currentZoom: 12
  });

  // Handle auto-visibility change callback
  const handleAutoVisibilityChange = useCallback((info) => {
    setAutoHideInfo({
      isAutoHidden: info.isAutoHidden,
      shouldAutoHide: info.shouldAutoHide,
      currentZoom: info.zoom
    });
  }, []);

  // Handle distance labels auto-enable callback
  const handleDistanceLabelsAutoEnable = useCallback((shouldEnable) => {
    if (shouldEnable) {
      setLayerVisibility(prev => ({
        ...prev,
        distanceLabels: true
      }));
    }
  }, []);

  // Use auto-hide distance labels hook
  const { 
    isAutoHidden, 
    cityZoomThreshold, 
    shouldAutoHide,
    forceUpdateVisibility 
  } = useAutoHideDistanceLabels(
    map, 
    mapReady, 
    layerVisibility.distanceLabels,
    handleAutoVisibilityChange,
    handleDistanceLabelsAutoEnable
  );

  // VRP-specific mode restrictions
  const isLayerDisabledForMode = useCallback((layerKey) => {
    switch (currentMode) {
      case 'Setup Path Points':
        // Setup mode: only allow location points control
        return layerKey !== 'locationPoints';
      
      case 'Custom Path':
        // Custom mode: auto path not operable
        return layerKey === 'autoPath';
      
      case 'Auto Find Path':
        // Auto mode: vehicle paths not operable (no custom paths yet)
        return layerKey === 'vehiclePaths';
      
      case 'Comparison Mode':
        // Comparison mode: all layers operable
        return false;
      
      default:
        return false;
    }
  }, [currentMode]);

  // Set individual layer visibility
  const setLayerVisibilityInternal = useCallback((layerKey, visible) => {
    if (!map || !mapReady) {
      console.warn('[VRP LayerControl] Map not ready');
      return false;
    }

    try {
      switch (layerKey) {
        case 'autoPath':
          if (map.getLayer(AUTO_PATH_LAYER)) {
            map.setLayoutProperty(AUTO_PATH_LAYER, 'visibility', visible ? 'visible' : 'none');
          }
          break;

        case 'vehiclePaths':
          // VRP uses UNFINISHED_PATH_LAYER for both completed and unfinished vehicle paths
          if (map.getLayer(UNFINISHED_PATH_LAYER)) {
            map.setLayoutProperty(UNFINISHED_PATH_LAYER, 'visibility', visible ? 'visible' : 'none');
          }
          // CUSTOM_PATH_LAYER is deprecated in VRP but hide it anyway
          if (map.getLayer(CUSTOM_PATH_LAYER)) {
            map.setLayoutProperty(CUSTOM_PATH_LAYER, 'visibility', 'none');
          }
          break;

        case 'locationPoints':
          // Control both circle and symbol layers together
          if (map.getLayer(POINTS_CIRCLE_LAYER)) {
            map.setLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility', visible ? 'visible' : 'none');
          }
          if (map.getLayer(POINTS_SYMBOL_LAYER)) {
            map.setLayoutProperty(POINTS_SYMBOL_LAYER, 'visibility', visible ? 'visible' : 'none');
          }
          break;

        case 'distanceLabels':
          // Distance labels need special handling due to auto-hide logic
          if (map.getLayer(DISTANCE_LABELS_LAYER)) {
            const currentZoom = map.getZoom();
            const shouldAutoHide = currentZoom <= 8; // City level threshold
            
            if (shouldAutoHide) {
              // At city level, unconditionally hide, don't allow user control
              map.setLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility', 'none');
              return false; // Return false to indicate operation blocked
            } else {
              // At non-city level, allow user control
              map.setLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility', visible ? 'visible' : 'none');
              // Immediately trigger re-evaluation of auto-hide logic
              setTimeout(() => {
                if (forceUpdateVisibility) {
                  forceUpdateVisibility();
                }
              }, 10);
            }
          }
          break;

        default:
          console.warn(`[VRP LayerControl] Unknown layer key: ${layerKey}`);
          return false;
      }

      return true;
    } catch (error) {
      console.error(`[VRP LayerControl] Error setting layer visibility for ${layerKey}:`, error);
      return false;
    }
  }, [map, mapReady]);

  // Toggle layer visibility state
  const toggleLayer = useCallback((layerKey) => {
    // Check if disabled due to mode
    if (isLayerDisabledForMode(layerKey)) {
      return false;
    }

    const newVisibility = !layerVisibility[layerKey];
    const success = setLayerVisibilityInternal(layerKey, newVisibility);
    
    if (success) {
      setLayerVisibility(prev => ({
        ...prev,
        [layerKey]: newVisibility
      }));
    }
    
    return success;
  }, [layerVisibility, setLayerVisibilityInternal, isLayerDisabledForMode]);

  // Show all layers
  const showAllLayers = useCallback(() => {
    const updates = {};
    let hasChanges = false;
    
    Object.keys(layerVisibility).forEach(key => {
      if (!layerVisibility[key] && !isLayerDisabledForMode(key)) {
        const success = setLayerVisibilityInternal(key, true);
        if (success) {
          updates[key] = true;
          hasChanges = true;
        }
      }
    });
    
    if (hasChanges) {
      setLayerVisibility(prev => ({ ...prev, ...updates }));
    }
  }, [layerVisibility, setLayerVisibilityInternal, isLayerDisabledForMode]);

  // Hide all layers (except location points which are always needed)
  const hideAllLayers = useCallback(() => {
    const updates = {};
    let hasChanges = false;
    
    Object.keys(layerVisibility).forEach(key => {
      // Don't hide location points as they're essential for VRP
      if (key !== 'locationPoints' && layerVisibility[key] && !isLayerDisabledForMode(key)) {
        const success = setLayerVisibilityInternal(key, false);
        if (success) {
          updates[key] = false;
          hasChanges = true;
        }
      }
    });
    
    if (hasChanges) {
      setLayerVisibility(prev => ({ ...prev, ...updates }));
    }
  }, [layerVisibility, setLayerVisibilityInternal, isLayerDisabledForMode]);

  // Sync current layer state from map
  const syncLayerVisibility = useCallback(() => {
    if (!map || !mapReady) return;

    try {
      const currentState = {};

      // Check auto path
      const autoPathLayer = map.getLayer(AUTO_PATH_LAYER);
      if (autoPathLayer) {
        currentState.autoPath = map.getLayoutProperty(AUTO_PATH_LAYER, 'visibility') !== 'none';
      }

      // Check vehicle paths (unfinished path layer)
      const vehiclePathLayer = map.getLayer(UNFINISHED_PATH_LAYER);
      if (vehiclePathLayer) {
        currentState.vehiclePaths = map.getLayoutProperty(UNFINISHED_PATH_LAYER, 'visibility') !== 'none';
      }

      // Check location points (both circle and symbol)
      const circleLayer = map.getLayer(POINTS_CIRCLE_LAYER);
      const symbolLayer = map.getLayer(POINTS_SYMBOL_LAYER);
      if (circleLayer && symbolLayer) {
        const circleVisible = map.getLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility') !== 'none';
        const symbolVisible = map.getLayoutProperty(POINTS_SYMBOL_LAYER, 'visibility') !== 'none';
        currentState.locationPoints = circleVisible && symbolVisible;
      }

      // Check distance labels
      const distanceLayer = map.getLayer(DISTANCE_LABELS_LAYER);
      if (distanceLayer) {
        currentState.distanceLabels = map.getLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility') !== 'none';
      }

      setLayerVisibility(prev => ({ ...prev, ...currentState }));
    } catch (error) {
      console.error('[VRP LayerControl] Error syncing layer visibility:', error);
    }
  }, [map, mapReady]);

  // Zoom-based auto-hide is now handled by useAutoHideDistanceLabels hook

  // Initialize layer visibility on mount
  useEffect(() => {
    if (mapReady) {
      syncLayerVisibility();
    }
  }, [mapReady, syncLayerVisibility]);

  return {
    layerVisibility,
    toggleLayer,
    showAllLayers,
    hideAllLayers,
    syncLayerVisibility,
    isLayerDisabledForMode,
    autoHideInfo,
    // VRP-specific additional info
    selectedVehicleInfo: {
      index: selectedVehicleIndex,
      hasSelection: selectedVehicleIndex !== null && selectedVehicleIndex !== undefined
    },
    pathStateInfo: {
      isComplete: pathComplete,
      mode: currentMode
    }
  };
};
